import { useState } from 'react';

import cn from 'classnames';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useTranslation } from 'react-i18next';

import { CodeInput } from 'components/CodeInput/CodeInput';
import { Button } from 'components/FormComponents';
import StatusPill from 'components/Pills/StatusPills';
import AsyncImage from 'components/async-image/async-image';

import { useGet2FASecretQuery } from 'api/user-api';

import styles from './TwoFactorAuthenticationSetup.module.scss';

export const TwoFactorAuthenticationSetup = ({
  user,
  company,
  onSuccess,
  setLoading = () => {},
  messagePopUp = () => {},
  onEnable2FA = () => {},
  onUserUpdate = () => {},
}) => {
  const { t } = useTranslation();
  const { data: mfaData } = useGet2FASecretQuery(user.id);

  const [configured, setConfigured] = useState(user.two_factor_auth_enabled);

  const disable2FA = () => {
    onUserUpdate({ two_factor_auth_secret: null });
    setConfigured(false);
  };

  const verifyTokenAndEnable = async code => {
    setLoading(true);
    const { payload: succed } = await onEnable2FA({ secret: mfaData.result.secret, code });

    if (succed && onSuccess) {
      onSuccess();
    } else if (succed) {
      setConfigured(true);
      messagePopUp(t('components.twoFactorAuthenticationSetup.success'), MESSAGE_STATE_SUCCESS);
    } else {
      messagePopUp(t('components.twoFactorAuthenticationSetup.error'), MESSAGE_STATE_ERROR);
    }

    setLoading(false);
  };

  return (
    <div>
      <div className={styles.configuration}>
        {configured ? (
          <div className={cn(styles.header, 'd-flex', 'flex-column', 'u-gap-large')}>
            <div className="d-flex flex-column align-items-left u-gap-small">
              <StatusPill status="success" text={t('common.enabled')} />
              {company?.enforce_two_factor_auth ? (
                <span className="u-font-size-small u-font-weight-500 primary-text">
                  {t('components.twoFactorAuthenticationSetup.companyLevelConfigured')}
                </span>
              ) : (
                <span className="u-font-size-small u-font-weight-500 primary-text">
                  {t('components.twoFactorAuthenticationSetup.configured')}
                </span>
              )}
            </div>
            <Button
              onClick={disable2FA}
              type="secondary"
              disabled={company?.enforce_two_factor_auth}>
              {t('components.twoFactorAuthenticationSetup.disable')}
            </Button>
            {company?.enforce_two_factor_auth && (
              <span className="u-font-size-x-small u-font-weight-400 secondary-text">
                {t('components.twoFactorAuthenticationSetup.enforce')}
              </span>
            )}
          </div>
        ) : (
          <>
            <div className={styles.header}>
              {t('components.twoFactorAuthenticationSetup.setup')}
            </div>
            <div className={styles.description}>
              {t('components.twoFactorAuthenticationSetup.description')}
            </div>
            <div className="u-margin-top">
              <div className={styles.step}>
                <span className={styles.stepOrder}>1</span>
                <p>{t('components.twoFactorAuthenticationSetup.stepOne')}</p>
              </div>
              <div className={styles.step}>
                <span className={styles.stepOrder}>2</span>
                <p>{t('components.twoFactorAuthenticationSetup.stepTwo')}</p>
              </div>
              <div className={cn(styles.step, styles.qrWrapper)}>
                <AsyncImage className={styles.qrImage} src={mfaData?.result?.qr} />
                <div className={styles.secretCode}>{mfaData?.result?.secret}</div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepOrder}>3</span>
                <p>{t('components.twoFactorAuthenticationSetup.stepThree')}</p>
              </div>
              <div>
                <CodeInput
                  className="u-margin-left--large"
                  onComplete={verifyTokenAndEnable}
                  autoFocus={false}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
